// =============================================================================
// io/sync/contracts.ts — the futures contract catalogue.
// -----------------------------------------------------------------------------
// A small, shared, read-only table (public SELECT to authenticated users, no
// write policy) that the whole app measures with: $/point turns MAE and MFE from
// bare points into money, tick size denominates the `ticks` display unit, and
// `findContract` resolves whatever a broker export called the instrument.
//
// ── 🎯 WHY A FAILED READ MUST THROW ──
//
// Every consumer of this catalogue degrades SILENTLY when it is empty, and each
// one degrades to a plausible number rather than to a visible gap:
//
//   `dollarsPerPointFor` returns 0     → an MAE of 12 points reads as "$0"
//   `tradeValue(unit: 'ticks')` → null → those trades drop OUT of the totals,
//                                        and the KPI strip quietly averages a
//                                        population nobody asked for
//   `findContract` returns null        → the 5×-P&L class of bug the longest-
//                                        prefix rule exists to prevent
//
// So an empty catalogue is never returned as though it were an answer. Same
// shape as accounts-without-adjustments and preferences-without-a-row: the safe
// value and the failure value are indistinguishable unless the reader refuses.
//
// ⚠ Deliberately NOT paged. This table is the exchange's product list — **55
// rows**, measured on the linked project 2026-09-06, against a 1,000-row cap —
// and it is seeded by an admin, not by users. If it ever approaches PAGE_ROWS
// the assertion below fails loudly rather than truncating in silence.
//
// ⚠ That number was "~80" here and "~100" in the provider, and both were
// guesses. A trader's OWN instruments are a separate table with its own cap;
// see `io/sync/userContracts.ts`.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { ContractSpec } from '../../domain/trade';
import { PAGE_ROWS } from './load';

/** What the catalogue carries beyond the spec: how to name it in a picker. */
export interface Contract extends ContractSpec {
  name: string;
  exchange: string;
}

export const CONTRACTS_SELECT =
  'symbol,name,exchange,tick_size,dollars_per_tick,dollars_per_point';

/**
 * ⚠ `numOrNull`, not `num`. `ContractSpec` types all three numerics as nullable
 * and `tradeValue` checks for null before dividing — coercing a missing tick
 * size to 0 would turn "this contract has no tick size on file" into a division
 * by zero and an Infinity in a P&L column.
 */
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function contractFromDb(r: Record<string, unknown>): Contract {
  return {
    symbol: String(r['symbol'] ?? '').toUpperCase(),
    name: String(r['name'] ?? ''),
    exchange: String(r['exchange'] ?? ''),
    tickSize: numOrNull(r['tick_size']),
    dollarsPerTick: numOrNull(r['dollars_per_tick']),
    dollarsPerPoint: numOrNull(r['dollars_per_point']),
  };
}

/**
 * The whole catalogue, ordered by symbol.
 *
 * ⚠ The order is presentation only — `findContract` is order-INDEPENDENT by
 * design, because alphabetical order always places `SI` ahead of `SIL` and a
 * first-match scan therefore priced Micro Silver as Silver. Sorting here is for
 * the contract pickers; nothing derives a number from it.
 */
export async function loadContracts(sb: SupabaseClient): Promise<Contract[]> {
  const { data, error } = await sb
    .from('contracts')
    .select(CONTRACTS_SELECT)
    .order('symbol', { ascending: true });

  if (error) {
    throw new Error(
      `loadContracts: ${error.message}. Refusing to continue with an empty catalogue — ` +
        `$/point would read as 0, so every MAE and MFE dollar value would show $0, and ` +
        `tick- and R-denominated totals would silently drop the trades they cannot value.`,
    );
  }

  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  if (rows.length >= PAGE_ROWS) {
    throw new Error(
      `loadContracts: got ${rows.length} rows, at or above the ${PAGE_ROWS}-row server cap. ` +
        `This read is unpaged because the catalogue is an admin-seeded product list; at this ` +
        `size it is being truncated, and a missing contract prices a trade at $0.`,
    );
  }

  return rows.map(contractFromDb);
}
